import type { Segment } from '@nono/contract';
import type { ReactNode } from 'react';
import { MetricInfoTrigger } from '@/components/data/MetricInfo';
import { CaveatRef } from '@/components/methodology/CaveatRef';
import { StatCard, type StatSource } from '@/components/ui/stat-card';
import { formatMetric } from '@/lib/format';
import { useViewMessages } from '@/lib/i18n';
import type { MethodologyId } from '@/lib/methodology';
import type { useFunnel, useMetrics } from '@/lib/queries';
import { cn } from '@/lib/utils';

type MetricsData = NonNullable<ReturnType<typeof useMetrics>['data']>;
type FunnelData = ReturnType<typeof useFunnel>['data'];

const MESSAGES = {
  nb: {
    spend: 'Annonseforbruk',
    members: 'Betalende medlemmer',
    cpa: 'CPA / medlem',
    revenue: 'Omsetning',
    cash: 'Kontant',
    roi: 'Omsetnings-ROI',
    leads: 'Leads',
    cpl: 'CPL',
    clicks: 'Klikk',
    ctr: 'CTR',
    cpc: 'CPC',
    bookings: 'Bookinger',
    costBooking: 'Kost / booking',
    subNew: 'nye',
    subRecurring: 'gjent.',
  },
  en: {
    spend: 'Ad spend',
    members: 'Paying members',
    cpa: 'CPA / member',
    revenue: 'Revenue',
    cash: 'Cash',
    roi: 'Revenue ROI',
    leads: 'Leads',
    cpl: 'CPL',
    clicks: 'Clicks',
    ctr: 'CTR',
    cpc: 'CPC',
    bookings: 'Bookings',
    costBooking: 'Cost / booking',
    subNew: 'new',
    subRecurring: 'recurring',
  },
};

interface Kpi {
  label: string;
  /** Registry key for the hover info-card (what / formula / source / confidence). */
  metricKey: string;
  value: number | null;
  format: 'nok' | 'int' | 'pct' | 'ratio';
  source: StatSource;
  caveat?: MethodologyId;
  /** Optional small second line, e.g. the new-vs-recurring split. */
  sub?: ReactNode;
}

/** The 6-card headline rail, shared by Overview + Funnel. Blended P&L (from
 * /funnel) in 'all'; Meta-side totals (from /metrics) in lead/booking.
 * `extra` appends one self-contained card (Overview's bookings drill-down) —
 * the xl column count stretches so the rail stays a single row. */
export function KpiRail({
  metrics,
  funnel,
  segment,
  extra,
}: {
  metrics: MetricsData;
  funnel: FunnelData;
  segment: Segment;
  extra?: ReactNode;
}) {
  const m = useViewMessages(MESSAGES);
  const kpis = buildKpis(m, segment, metrics, funnel);
  return (
    <div
      className={cn(
        'grid grid-cols-2 gap-3 lg:grid-cols-3',
        extra ? 'xl:grid-cols-7' : 'xl:grid-cols-6',
      )}
    >
      {kpis.map((k) => (
        <StatCard
          key={k.label}
          label={<MetricInfoTrigger metricKey={k.metricKey} label={k.label} />}
          source={k.source}
          value={
            <span className="inline-flex flex-col">
              <span className="inline-flex items-center gap-1">
                <span className="text-title">{formatMetric(k.value, k.format)}</span>
                {k.caveat ? <CaveatRef id={k.caveat} /> : null}
              </span>
              {k.sub ? (
                <span className="mt-0.5 text-micro font-normal text-fg-subtle">{k.sub}</span>
              ) : null}
            </span>
          }
        />
      ))}
      {extra}
    </div>
  );
}

function buildKpis(
  m: (typeof MESSAGES)['nb'],
  segment: Segment,
  data: MetricsData,
  funnel: FunnelData,
): Kpi[] {
  if (data.segment === 'all') {
    const isAllFunnel = funnel && !('segment' in funnel);
    const totals = isAllFunnel ? funnel.totals : null;
    const derived = isAllFunnel ? funnel.derived : null;
    return [
      {
        label: m.spend,
        metricKey: 'adSpend',
        value: totals?.spend ?? data.totals?.adSpend ?? null,
        format: 'nok',
        source: 'meta',
      },
      {
        label: m.members,
        metricKey: 'soldMemberships',
        value: totals?.sold ?? data.totals?.soldMemberships ?? null,
        format: 'int',
        source: 'momence',
        sub:
          data.totals && data.totals.newMembers + data.totals.recurringMembers > 0 ? (
            <>
              {formatMetric(data.totals.newMembers, 'int')} {m.subNew} ·{' '}
              {formatMetric(data.totals.recurringMembers, 'int')} {m.subRecurring}
            </>
          ) : undefined,
      },
      {
        label: m.cpa,
        metricKey: 'cpa',
        value: derived?.cpa ?? data.totals?.cpa ?? null,
        format: 'nok',
        source: 'derived',
        caveat: 'trustMeta',
      },
      {
        label: m.revenue,
        metricKey: 'revenue',
        value: totals?.revenue ?? data.totals?.revenue ?? null,
        format: 'nok',
        source: 'stripe',
        caveat: 'revenueEqualsCash',
        sub:
          data.totals && data.totals.newRevenue + data.totals.recurringRevenue > 0 ? (
            <>
              {formatMetric(data.totals.newRevenue, 'nok')} {m.subNew} ·{' '}
              {formatMetric(data.totals.recurringRevenue, 'nok')} {m.subRecurring}
            </>
          ) : undefined,
      },
      {
        label: m.cash,
        metricKey: 'cash',
        value: totals?.cash ?? data.totals?.cash ?? null,
        format: 'nok',
        source: 'stripe',
      },
      {
        label: m.roi,
        metricKey: 'rRoi',
        value: derived?.rRoi ?? data.totals?.rRoi ?? null,
        format: 'ratio',
        source: 'derived',
        caveat: 'roiFirstMonth',
      },
    ];
  }

  const tot = data.totals;
  const common: Kpi[] = [
    {
      label: m.spend,
      metricKey: 'adSpend',
      value: tot?.adSpend ?? null,
      format: 'nok',
      source: 'meta',
    },
  ];
  if (segment === 'lead') {
    return [
      ...common,
      {
        label: m.leads,
        metricKey: 'leads',
        value: tot?.leads ?? null,
        format: 'int',
        source: 'meta',
      },
      { label: m.cpl, metricKey: 'cpl', value: tot?.cpl ?? null, format: 'nok', source: 'meta' },
      {
        label: m.clicks,
        metricKey: 'clicks',
        value: tot?.clicks ?? null,
        format: 'int',
        source: 'meta',
      },
      { label: m.ctr, metricKey: 'ctr', value: tot?.ctr ?? null, format: 'pct', source: 'meta' },
      { label: m.cpc, metricKey: 'cpc', value: tot?.cpc ?? null, format: 'nok', source: 'meta' },
    ];
  }
  return [
    ...common,
    {
      label: m.bookings,
      metricKey: 'bookings',
      value: tot?.bookings ?? null,
      format: 'int',
      source: 'meta',
      caveat: 'bookingIsMeta',
    },
    {
      label: m.costBooking,
      metricKey: 'costPerBooking',
      value: tot?.costPerBooking ?? null,
      format: 'nok',
      source: 'meta',
    },
    {
      label: m.clicks,
      metricKey: 'clicks',
      value: tot?.clicks ?? null,
      format: 'int',
      source: 'meta',
    },
    { label: m.ctr, metricKey: 'ctr', value: tot?.ctr ?? null, format: 'pct', source: 'meta' },
    { label: m.cpc, metricKey: 'cpc', value: tot?.cpc ?? null, format: 'nok', source: 'meta' },
  ];
}
